import {
	Children,
	ComponentProps,
	ComponentRef,
	isValidElement,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react"
import useInfiniteScroll, {
	type UseInfiniteScrollHookArgs,
} from "react-infinite-scroll-hook"
import { cn } from "styles/cn"

export const Scrollable = Object.assign(
	{},
	{
		ReverseHorizontal,
		ReverseVertical,
	},
)

const DISABLED_SCROLL: UseInfiniteScrollHookArgs = {
	loading: false,
	hasNextPage: false,
	onLoadMore: () => {},
}

/**
 * ReverseHorizontal provides a horizontal reverse/bidirectional scrolling container:
 * 1. By default, scrolls to the far right on initial load (when scrollEnd is not configured),
 *    keeping the most recent items in view.
 * 2. Anchors the scroll position when older items are prepended to the left (infinite scroll),
 *    preventing on-screen items from shifting.
 * 3. Supports bidirectional infinite scrolling (start/left and end/right) while gating triggers
 *    until user interaction to prevent automatic overshoot loops.
 */
function ReverseHorizontal({
	className,
	children,
	size,
	scroll,
	scrollEnd,
	...props
}: ComponentProps<"ul"> & {
	size: number
	scroll: UseInfiniteScrollHookArgs
	scrollEnd?: UseInfiniteScrollHookArgs
}) {
	const scrollableRootRef = useRef<ComponentRef<"div"> | null>(null)
	// Distance in pixels from the right edge of the content to the left edge of the viewport (scrollWidth - scrollLeft)
	const lastScrollDistanceToRightRef = useRef<number>(0)
	// Identify the first child key to detect prepend vs append operations
	const firstChild = Children.toArray(children)[0]
	const firstKey = isValidElement(firstChild) ? firstChild.key : undefined
	const previousFirstKey = useRef(firstKey)
	// Previous item count to detect initial render vs subsequent data updates
	const previousSize = useRef(0)
	// Track whether the user has actively interacted with the scroll container
	const userInteractedRef = useRef<boolean>(false)
	// Gating flags to prevent infinite scroll hooks from triggering before user interaction and threshold checks
	const [canLoadStart, setCanLoadStart] = useState<boolean>(false)
	const [canLoadEnd, setCanLoadEnd] = useState<boolean>(false)

	// Infinite scroll hook for loading earlier items when scrolling towards the left/start
	const [infiniteRef, { rootRef }] = useInfiniteScroll({
		...scroll,
		disabled: scroll.disabled || !canLoadStart,
	})
	// Infinite scroll hook for loading later items when scrolling towards the right/end
	const [endInfiniteRef, { rootRef: endRootRef }] = useInfiniteScroll({
		...(scrollEnd ?? DISABLED_SCROLL),
		disabled: scrollEnd?.disabled || !canLoadEnd,
	})

	// Track horizontal scroll events to update distance from right and gate infinite scroll loading
	const handleRootScroll = useCallback(() => {
		const rootNode = scrollableRootRef.current
		if (!rootNode) {
			return
		}
		const scrollDistanceToRight = rootNode.scrollWidth - rootNode.scrollLeft
		lastScrollDistanceToRightRef.current = scrollDistanceToRight

		const hasOverflow = rootNode.scrollWidth > rootNode.clientWidth + 1
		if (!hasOverflow || !userInteractedRef.current) {
			setCanLoadStart(false)
			setCanLoadEnd(false)
			return
		}

		// Calculate proximity to left (start) and right (end) scroll boundaries
		const distanceFromLeft = rootNode.scrollLeft
		const distanceFromRight =
			rootNode.scrollWidth - rootNode.scrollLeft - rootNode.clientWidth

		// Allow loading more items when within 50px of either edge
		setCanLoadStart(distanceFromLeft <= 50)
		setCanLoadEnd(distanceFromRight <= 50)
	}, [])

	useLayoutEffect(() => {
		const scrollableRoot = scrollableRootRef.current
		if (!scrollableRoot) {
			return
		}
		const isInitial = previousSize.current === 0
		const appended =
			previousSize.current > 0 && firstKey === previousFirstKey.current
		previousFirstKey.current = firstKey
		previousSize.current = size

		// Initial load: scroll to the far right (if scrollEnd is not used) and record initial right anchor
		if (isInitial) {
			if (!scrollEnd) {
				scrollableRoot.scrollLeft =
					scrollableRoot.scrollWidth - scrollableRoot.clientWidth
			}
			lastScrollDistanceToRightRef.current =
				scrollableRoot.scrollWidth - scrollableRoot.scrollLeft
			return
		}

		// When items are prepended at the left, preserve viewport relative to the right edge
		const lastScrollDistanceToRight = lastScrollDistanceToRightRef.current
		if (!appended) {
			scrollableRoot.scrollLeft =
				scrollableRoot.scrollWidth - lastScrollDistanceToRight
		}
		lastScrollDistanceToRightRef.current =
			scrollableRoot.scrollWidth - scrollableRoot.scrollLeft
	}, [firstKey, size, rootRef, scrollEnd])

	// Listen for user input events (wheel, touch, pointer, keyboard) to flag intentional user interaction
	useEffect(() => {
		const scrollableRoot = scrollableRootRef.current
		if (!scrollableRoot) {
			return
		}
		const onUserScrollStart = () => {
			userInteractedRef.current = true
		}

		scrollableRoot.addEventListener("wheel", onUserScrollStart, {
			passive: true,
		})
		scrollableRoot.addEventListener("touchmove", onUserScrollStart, {
			passive: true,
		})
		scrollableRoot.addEventListener("pointerdown", onUserScrollStart, {
			passive: true,
		})
		scrollableRoot.addEventListener("keydown", onUserScrollStart, {
			passive: true,
		})

		return () => {
			scrollableRoot.removeEventListener("wheel", onUserScrollStart)
			scrollableRoot.removeEventListener("touchmove", onUserScrollStart)
			scrollableRoot.removeEventListener("pointerdown", onUserScrollStart)
			scrollableRoot.removeEventListener("keydown", onUserScrollStart)
		}
	}, [])

	// Combine infinite scroll trigger refs and the internal DOM reference
	const rootRefSetter = useCallback(
		(node: HTMLDivElement) => {
			rootRef(node)
			endRootRef(node)
			scrollableRootRef.current = node
		},
		[endRootRef, rootRef],
	)

	return (
		<div
			ref={rootRefSetter}
			className={cn("flex overflow-x-auto", className)}
			onScroll={handleRootScroll}
		>
			{/* Sentinel for loading items at the start (left) */}
			{scroll.hasNextPage && canLoadStart && <div ref={infiniteRef} />}

			<ul {...props} className="flex justify-center gap-3">
				<div className="w-1 shrink-0" />
				{children}
				<div className="w-1 shrink-0" />
			</ul>

			{/* Sentinel for loading items at the end (right) */}
			{scrollEnd?.hasNextPage && canLoadEnd && <div ref={endInfiniteRef} />}
		</div>
	)
}

/**
 * ReverseVertical provides a reverse-scrolling container (e.g. for chat feeds or timelines):
 * 1. Automatically scrolls to the bottom on initial load so the newest messages are in view.
 * 2. Anchors the scroll position when older items are prepended at the top (infinite scroll),
 *    preventing the content currently on screen from jumping.
 * 3. Keeps the viewport pinned to the bottom when new items are appended or when content
 *    dimensions change (e.g. images / media asynchronously loading).
 */
function ReverseVertical({
	className,
	children,
	size,
	scroll,
	scrollToBottom: shouldScrollToBottom = true,
	...props
}: ComponentProps<"div"> & {
	size: number
	scroll: UseInfiniteScrollHookArgs
	scrollToBottom?: boolean
}) {
	const [isInitialized, setIsInitialized] = useState(false)
	const [infiniteRef, { rootRef }] = useInfiniteScroll(scroll)
	const scrollableRootRef = useRef<ComponentRef<"div"> | null>(null)

	// Distance in pixels from the bottom of the content to the top of the viewport (scrollHeight - scrollTop)
	const lastScrollDistanceToBottomRef = useRef<number>(0)
	// Previous item count to detect initial load vs prepend vs append
	const previousSizeRef = useRef<number>(0)
	// Track whether the user has intentionally scrolled up away from the bottom
	const userScrolledUpRef = useRef<boolean>(false)
	// Track whether the scroll was initiated by actual user input (wheel, touch, pointer, key)
	const userInteractedRef = useRef<boolean>(false)
	// Track if initial scroll to bottom has executed
	const hasInitializedRef = useRef<boolean>(false)

	// Scrolls viewport all the way to the bottom and records the new bottom anchor position
	const scrollToBottom = useCallback(() => {
		const scrollableRoot = scrollableRootRef.current
		if (!scrollableRoot) {
			return
		}
		scrollableRoot.scrollTop =
			scrollableRoot.scrollHeight - scrollableRoot.clientHeight
		lastScrollDistanceToBottomRef.current =
			scrollableRoot.scrollHeight - scrollableRoot.scrollTop
		userScrolledUpRef.current = false
		userInteractedRef.current = false
	}, [])

	// Maintains the user's view position relative to the bottom after older items are prepended
	const anchorScrollPosition = useCallback(() => {
		const scrollableRoot = scrollableRootRef.current
		if (!scrollableRoot) {
			return
		}
		const lastScrollDistanceToBottom = lastScrollDistanceToBottomRef.current
		scrollableRoot.scrollTop =
			scrollableRoot.scrollHeight - lastScrollDistanceToBottom
		lastScrollDistanceToBottomRef.current =
			scrollableRoot.scrollHeight - scrollableRoot.scrollTop
	}, [])

	// Track scroll events to update distance from bottom and determine if the user has scrolled up
	const handleRootScroll = useCallback(() => {
		const rootNode = scrollableRootRef.current
		if (!rootNode) {
			return
		}

		// Calculate distance from bottom of viewport to bottom of content
		const distanceFromBottom =
			rootNode.scrollHeight - rootNode.scrollTop - rootNode.clientHeight
		const scrollDistanceToBottom = rootNode.scrollHeight - rootNode.scrollTop

		lastScrollDistanceToBottomRef.current = scrollDistanceToBottom

		// User is considered at the bottom if within 30px of the container's bottom edge
		if (distanceFromBottom <= 30) {
			userScrolledUpRef.current = false
			userInteractedRef.current = false
		} else if (userInteractedRef.current) {
			userScrolledUpRef.current = true
		}
	}, [])

	useLayoutEffect(() => {
		const scrollableRoot = scrollableRootRef.current
		if (!scrollableRoot) {
			return
		}

		const isInitialLoad = !hasInitializedRef.current && size > 0
		const isDecreased =
			previousSizeRef.current > 0 && size < previousSizeRef.current
		const isPrepended =
			hasInitializedRef.current &&
			userScrolledUpRef.current &&
			size > previousSizeRef.current

		previousSizeRef.current = size

		// If list is emptied, reset tracking so the next load scrolls to bottom
		if (size === 0) {
			hasInitializedRef.current = false
			userScrolledUpRef.current = false
			userInteractedRef.current = false
			lastScrollDistanceToBottomRef.current = 0
			setIsInitialized(false)
			return
		}

		// Case 1: Initial load, item count reset, or user is currently at the bottom -> scroll to bottom
		if (
			shouldScrollToBottom &&
			(isInitialLoad || isDecreased || !userScrolledUpRef.current)
		) {
			hasInitializedRef.current = true
			userScrolledUpRef.current = false
			userInteractedRef.current = false
			scrollToBottom()
			if (!isInitialized) {
				setIsInitialized(true)
			}
			const rafId = requestAnimationFrame(() => {
				scrollToBottom()
				if (!isInitialized) {
					setIsInitialized(true)
				}
			})
			return () => cancelAnimationFrame(rafId)
		}

		// Case 2: Older items were prepended at the top while user is scrolled up -> maintain exact scroll distance
		if (isPrepended) {
			anchorScrollPosition()
			const rafId = requestAnimationFrame(anchorScrollPosition)
			return () => cancelAnimationFrame(rafId)
		}
	}, [
		size,
		shouldScrollToBottom,
		scrollToBottom,
		anchorScrollPosition,
		isInitialized,
	])

	const contentRef = useRef<HTMLDivElement | null>(null)

	// Observe container and content resize events as well as media load events
	// to keep viewport pinned to bottom (or anchor position if scrolled up)
	useEffect(() => {
		const scrollableRoot = scrollableRootRef.current
		const contentNode = contentRef.current
		if (!scrollableRoot || !shouldScrollToBottom) {
			return
		}

		const handleLayoutChange = () => {
			if (!userScrolledUpRef.current) {
				scrollToBottom()
			} else {
				anchorScrollPosition()
			}
		}

		const onUserScrollStart = () => {
			userInteractedRef.current = true
		}

		const resizeObserver = new ResizeObserver(handleLayoutChange)
		resizeObserver.observe(scrollableRoot)
		if (contentNode) {
			resizeObserver.observe(contentNode)
		}

		scrollableRoot.addEventListener("load", handleLayoutChange, true)
		scrollableRoot.addEventListener("error", handleLayoutChange, true)
		scrollableRoot.addEventListener("wheel", onUserScrollStart, {
			passive: true,
		})
		scrollableRoot.addEventListener("touchmove", onUserScrollStart, {
			passive: true,
		})
		scrollableRoot.addEventListener("pointerdown", onUserScrollStart, {
			passive: true,
		})
		scrollableRoot.addEventListener("keydown", onUserScrollStart, {
			passive: true,
		})

		return () => {
			resizeObserver.disconnect()
			scrollableRoot.removeEventListener("load", handleLayoutChange, true)
			scrollableRoot.removeEventListener("error", handleLayoutChange, true)
			scrollableRoot.removeEventListener("wheel", onUserScrollStart)
			scrollableRoot.removeEventListener("touchmove", onUserScrollStart)
			scrollableRoot.removeEventListener("pointerdown", onUserScrollStart)
			scrollableRoot.removeEventListener("keydown", onUserScrollStart)
		}
	}, [shouldScrollToBottom, scrollToBottom, anchorScrollPosition])

	// Combine infinite scroll trigger ref and our internal DOM reference
	const rootRefSetter = useCallback(
		(node: HTMLDivElement) => {
			rootRef(node)
			scrollableRootRef.current = node
		},
		[rootRef],
	)

	return (
		<div
			{...props}
			ref={rootRefSetter}
			className={cn("overflow-y-auto", className)}
			onScroll={handleRootScroll}
		>
			{/* Sentinel element at the top: only active after initial bottom positioning */}
			{scroll.hasNextPage && isInitialized && <div ref={infiniteRef} />}

			<div ref={contentRef}>{children}</div>
		</div>
	)
}
